import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import StatusBadge from './common/StatusBadge';
import EmptyState from './common/EmptyState';
import LoadingState from './common/LoadingState';
import InlineError from './common/InlineError';
import CustomerFavoritesTable from './child/CustomerFavoritesTable';
import '../assets/css/business-overview.css';
import { formatDisplayDate } from '../utils/formatDate';

const MetricCard = ({ icon, tone, label, primaryValue, secondaryText }) => (
    <article className="bo-metric-card">
        <div className={`bo-icon bo-icon-${tone}`} aria-hidden="true">
            <Icon icon={icon} />
        </div>
        <div className="bo-metric-copy">
            <span className="bo-eyebrow">{label}</span>
            <div className="bo-metric-value" title={secondaryText ? `${primaryValue} | ${secondaryText}` : String(primaryValue)}>
                <strong>{primaryValue}</strong>
                {secondaryText && (
                    <>
                        <span className="bo-metric-sep">|</span>
                        <span className="bo-metric-inline-sub">{secondaryText}</span>
                    </>
                )}
            </div>
        </div>
    </article>
);

const BusinessOverviewLayer = () => {
    const { auth, user } = useAuth();
    const [venues, setVenues] = useState([]);
    const [limits, setLimits] = useState(null);
    const [favoritesSummary, setFavoritesSummary] = useState(null);
    const [reviewsSummary, setReviewsSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [venuesRes, limitsRes, favoritesRes, reviewsRes] = await Promise.all([
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/my-venues`),
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/limits`),
                auth.fetch(`${config.API_BASE_URL}/api/actions/business/favorites`).catch(() => null),
                auth.fetch(`${config.API_BASE_URL}/api/v2/venues/my-venues/reviews-summary`).catch(() => null)
            ]);

            if (!venuesRes.ok) throw new Error('Failed to load venue data');

            const venuesData = await venuesRes.json();
            setVenues(Array.isArray(venuesData) ? venuesData : (venuesData.venues || []));

            if (limitsRes.ok) setLimits(await limitsRes.json());

            if (favoritesRes?.ok) {
                const favoritesData = await favoritesRes.json();
                setFavoritesSummary(favoritesData.summary || null);
            }

            if (reviewsRes?.ok) {
                const reviewsData = await reviewsRes.json();
                setReviewsSummary(reviewsData || null);
            }
        } catch (err) {
            console.error('Error loading business overview:', err);
            setError(err.message || 'Unable to load your dashboard data');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const venueLimit = limits?.limits?.venues;
    const planName = limits?.planName || 'Free';
    const canAddVenue = venueLimit
        ? venueLimit.max === -1 || venueLimit.current < venueLimit.max
        : true;
    const quotaPercentage = venueLimit && venueLimit.max !== -1
        ? Math.min(Math.round((venueLimit.current / venueLimit.max) * 100), 100)
        : 0;

    const pendingCount = venues.filter((venue) => venue.verification_status === 'pending').length;
    const approvedCount = venues.filter((venue) => venue.verification_status === 'approved').length;
    const rejectedCount = venues.filter((venue) => venue.verification_status === 'rejected').length;
    const suspendedCount = venues.filter((venue) => ['suspended', 'banned'].includes(venue.verification_status)).length;
    const totalReviews = venues.reduce((sum, venue) => sum + (parseInt(venue.review_count, 10) || 0), 0);
    const ratedVenues = venues.filter((venue) => (parseInt(venue.review_count, 10) || 0) > 0 && parseFloat(venue.average_rating) > 0);
    const overallRating = ratedVenues.length
        ? (ratedVenues.reduce((sum, venue) => sum + parseFloat(venue.average_rating), 0) / ratedVenues.length).toFixed(1)
        : null;

    const favoritesCount = favoritesSummary?.total_favorites ?? 0;
    const uniqueCustomers = favoritesSummary?.unique_users_count ?? favoritesSummary?.unique_customers ?? 0;

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    const firstName = (user?.full_name || 'Business partner').trim().split(/\s+/)[0];

    const verificationParts = [
        approvedCount ? `${approvedCount} approved` : null,
        pendingCount ? `${pendingCount} in review` : null,
        rejectedCount ? `${rejectedCount} rejected` : null,
        suspendedCount ? `${suspendedCount} suspended` : null,
    ].filter(Boolean);

    const verificationPrimary = loading
        ? '—'
        : (verificationParts[0] || 'Not submitted');
    const verificationSecondary = loading
        ? null
        : (verificationParts.length > 1 ? verificationParts.slice(1).join(' | ') : null);

    const verificationTone = rejectedCount || suspendedCount ? 'rose' : pendingCount ? 'amber' : 'green';
    const venuesNeedingAttention = venues.filter((venue) =>
        ['rejected', 'suspended', 'banned'].includes(venue.verification_status)
    );

    const formatIntlDate = (dateVal) => {
        if (!dateVal) return '';
        const d = new Date(dateVal);
        if (Number.isNaN(d.getTime())) return '';
        return formatDisplayDate(d);
    };

    return (
        <div className="bo-page">
            {error && <InlineError message={error} onRetry={fetchData} />}

            <section className="bo-welcome" aria-labelledby="business-welcome-title">
                <div className="bo-welcome-copy">
                    <h2 id="business-welcome-title">{getGreeting()}, {firstName}</h2>
                    <p>Manage your venues, monitor customer interest, and keep your listings ready for discovery.</p>
                </div>
                <div className="bo-welcome-actions">
                    <Link to={canAddVenue ? '/add-venue' : '/pricing'} className="hs-btn-primary">
                        <Icon icon={canAddVenue ? 'solar:add-circle-linear' : 'solar:crown-linear'} />
                        {canAddVenue ? 'Add Venue' : 'Upgrade Plan'}
                    </Link>
                    <Link to="/my-venues" className="hs-btn-secondary">
                        <Icon icon="solar:shop-2-linear" />
                        Manage Venues
                    </Link>
                </div>
            </section>

            {venuesNeedingAttention.length > 0 && (
                <section className="hs-modern-card border border-danger-200" aria-labelledby="verification-action-title">
                    <div className="bo-card-header">
                        <div>
                            <span className="bo-eyebrow text-danger-600">Action required</span>
                            <h3 id="verification-action-title">Resolve venue verification issues</h3>
                            <p>Review the moderator’s reason. Rejected listings can be corrected and resubmitted; suspended or banned listings require support.</p>
                        </div>
                    </div>
                    <div className="d-flex flex-column gap-3 p-3">
                        {venuesNeedingAttention.map((venue) => (
                            <div key={venue.id} className="d-flex align-items-start justify-content-between gap-3 p-3 bg-neutral-50 radius-12">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <strong title={venue.title || venue.business_name}>{venue.title || venue.business_name}</strong>
                                        <StatusBadge status={venue.verification_status} />
                                    </div>
                                    <small className="text-secondary-light">
                                        {venue.verification_notes || 'Contact support for more information about this decision.'}
                                    </small>
                                </div>
                                {venue.verification_status === 'rejected' && (
                                    <Link to={`/edit-venue/${venue.id}`} className="hs-btn-primary hs-btn-sm flex-shrink-0">
                                        Fix Listing
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="bo-metrics" aria-label="Business metrics">
                <MetricCard
                    icon="solar:shop-2-linear"
                    tone="blue"
                    label="Active Venues"
                    primaryValue={loading ? '—' : venues.length}
                    secondaryText={loading ? null : `${approvedCount} verified`}
                />
                <MetricCard
                    icon="solar:shield-check-linear"
                    tone={verificationTone}
                    label="Verification"
                    primaryValue={verificationPrimary}
                    secondaryText={verificationSecondary}
                />
                <MetricCard
                    icon="solar:heart-linear"
                    tone="rose"
                    label="Customer Saves"
                    primaryValue={loading ? '—' : favoritesCount}
                    secondaryText={loading ? null : `${uniqueCustomers} unique ${uniqueCustomers === 1 ? 'customer' : 'customers'}`}
                />
                <MetricCard
                    icon="solar:star-linear"
                    tone="amber"
                    label="Average Rating"
                    primaryValue={loading ? '—' : (overallRating ? `${overallRating} / 5` : 'No rating')}
                    secondaryText={loading ? null : (totalReviews > 0 ? `${totalReviews} ${totalReviews === 1 ? 'review' : 'reviews'}` : 'No reviews')}
                />
            </section>

            <div className="bo-primary-grid">
                <section className="hs-modern-card bo-venues-card" aria-labelledby="venues-title">
                    <header className="bo-engagement-header">
                        <div className="bo-engagement-title">
                            <div className="bo-icon bo-icon-blue" aria-hidden="true">
                                <Icon icon="solar:shop-2-bold" />
                            </div>
                            <div>
                                <h3 id="venues-title">Your Venues</h3>
                                <p>Listings currently managed through HubScroll.</p>
                            </div>
                        </div>
                        <Link to="/my-venues" className="bo-text-link">View All <Icon icon="solar:arrow-right-linear" /></Link>
                    </header>

                    {loading ? (
                        <LoadingState message="Loading venues..." />
                    ) : venues.length === 0 ? (
                        <EmptyState
                            icon="solar:shop-2-linear"
                            title="Create your first venue"
                            description="Add your venue details to start building your HubScroll presence."
                            action={<Link to="/add-venue" className="hs-btn-primary"><Icon icon="solar:add-circle-linear" /> Add Venue</Link>}
                        />
                    ) : (
                        <div className="bo-table-scroll">
                            <table className="bo-table">
                                <thead>
                                    <tr><th>Venue</th><th>Location</th><th>Rating</th><th>Status</th><th><span className="visually-hidden">Actions</span></th></tr>
                                </thead>
                                <tbody>
                                    {venues.slice(0, 4).map((venue) => {
                                        const vReviews = parseInt(venue.review_count, 10) || 0;
                                        const vRating = vReviews > 0 ? parseFloat(venue.average_rating) || 0 : 0;
                                        const fullVenueTitle = venue.title || venue.business_name || `Venue #${venue.id}`;
                                        return (
                                            <tr key={venue.id}>
                                                <td>
                                                    <div className="bo-venue-name">
                                                        <span className="bo-venue-icon"><Icon icon="solar:shop-2-linear" /></span>
                                                        <div className="overflow-hidden">
                                                            <strong className="text-truncate" title={fullVenueTitle}>{fullVenueTitle}</strong>
                                                            <small>Venue #{venue.id}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td><span className="bo-cell-muted">{venue.address || venue.city || 'Location pending'}</span></td>
                                                <td>
                                                    {vReviews > 0 && vRating > 0 ? (
                                                        <span className="bo-rating">
                                                            <Icon icon="solar:star-bold" /> {vRating.toFixed(1)} <small>({vReviews} {vReviews === 1 ? 'review' : 'reviews'})</small>
                                                        </span>
                                                    ) : (
                                                        <span className="bo-cell-muted">No rating</span>
                                                    )}
                                                </td>
                                                <td><StatusBadge status={venue.verification_status} /></td>
                                                <td>
                                                    <div className="bo-row-actions">
                                                        <Link to={`/venue/${venue.id}`} aria-label={`View ${fullVenueTitle}`} title="View Venue"><Icon icon="solar:eye-linear" /></Link>
                                                        <Link to={`/edit-venue/${venue.id}`} aria-label={`Edit ${fullVenueTitle}`} title="Edit Venue"><Icon icon="solar:pen-linear" /></Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <aside className="hs-modern-card bo-plan-card" aria-labelledby="plan-title">
                    <div className="bo-plan-icon"><Icon icon="solar:crown-star-linear" /></div>
                    <span className="bo-eyebrow">Current Plan</span>
                    <h3 id="plan-title">{planName}</h3>
                    <p>Track your listing capacity and upgrade only when your business needs more room.</p>

                    {venueLimit && venueLimit.max !== -1 ? (
                        <div className="bo-quota">
                            <div><span>Venue capacity</span><strong>{venueLimit.current} / {venueLimit.max}</strong></div>
                            <div className="bo-quota-track" role="progressbar" aria-valuenow={venueLimit.current} aria-valuemin="0" aria-valuemax={venueLimit.max}><i style={{ width: `${quotaPercentage}%` }} /></div>
                            <small>{Math.max(venueLimit.max - venueLimit.current, 0)} venue {Math.max(venueLimit.max - venueLimit.current, 0) === 1 ? 'slot' : 'slots'} remaining</small>
                        </div>
                    ) : (
                        <div className="bo-unlimited"><Icon icon="solar:infinity-bold" /> Unlimited venue listings</div>
                    )}

                    <Link to="/pricing" className="hs-btn-secondary bo-plan-action">View Plans <Icon icon="solar:arrow-right-linear" /></Link>
                </aside>
            </div>

            <CustomerFavoritesTable
                endpoint={`${config.API_BASE_URL}/api/actions/business/favorites`}
                title="Customer Engagement"
                subtitle="See who is saving your venues and content in the mobile app."
                isAdmin={false}
            />

            {/* ── Ratings & Reviews Summary ── */}
            {reviewsSummary && (
                <section className="hs-modern-card bo-engagement" aria-labelledby="reviews-section-title">
                    <header className="bo-engagement-header">
                        <div className="bo-engagement-title">
                            <div className="bo-icon bo-icon-amber" aria-hidden="true">
                                <Icon icon="solar:star-bold" />
                            </div>
                            <div>
                                <h3 id="reviews-section-title">Ratings &amp; Reviews</h3>
                                <p>Per-venue rating breakdown and your most recent customer reviews.</p>
                            </div>
                        </div>
                    </header>

                    {/* Per-venue rating mini-cards */}
                    {reviewsSummary.venues?.length > 0 && (
                        <div className="p-3 p-md-4 bg-neutral-50 border-bottom">
                            <div className="row g-3">
                                {reviewsSummary.venues.map((v) => {
                                    const matchedVenue = venues.find((vn) => Number(vn.id) === Number(v.venue_id));
                                    const fullVenueName = matchedVenue?.title || matchedVenue?.business_name || v.venue_name || `Venue #${v.venue_id}`;
                                    const vReviewCount = parseInt(v.review_count, 10) || 0;
                                    const vAvgRating = vReviewCount > 0 ? (parseFloat(v.average_rating) || 0) : 0;

                                    return (
                                        <div key={v.venue_id} className="col-12 col-sm-6 col-lg-4">
                                            <div
                                                className="d-flex align-items-center gap-3 p-3 bg-white border border-neutral-200"
                                                style={{ borderRadius: '14px' }}
                                            >
                                                <div
                                                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{ width: '40px', height: '40px', borderRadius: '11px', backgroundColor: 'rgba(7,137,213,0.1)', color: '#0789d5' }}
                                                >
                                                    <Icon icon="solar:shop-2-linear" style={{ fontSize: '20px' }} />
                                                </div>
                                                <div className="overflow-hidden flex-grow-1">
                                                    <span
                                                        className="fw-semibold text-primary-light d-block text-sm text-truncate"
                                                        style={{ lineHeight: 1.3 }}
                                                        title={fullVenueName}
                                                    >
                                                        {fullVenueName}
                                                    </span>
                                                    <div className="d-flex align-items-center gap-2 mt-1">
                                                        {vReviewCount > 0 && vAvgRating > 0 ? (
                                                            <>
                                                                <span
                                                                    className="badge rounded-pill px-2 py-1 text-xs fw-bold d-inline-flex align-items-center gap-1"
                                                                    style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#d97706' }}
                                                                >
                                                                    <Icon icon="solar:star-bold" style={{ fontSize: '11px' }} />
                                                                    {vAvgRating.toFixed(1)}
                                                                </span>
                                                                <span className="text-xs text-secondary-light">
                                                                    {vReviewCount} {vReviewCount === 1 ? 'review' : 'reviews'}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span className="text-xs text-secondary-light">No rating</span>
                                                                <span className="text-xs text-secondary-light">|</span>
                                                                <span className="text-xs text-secondary-light">No reviews</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Recent reviews list */}
                    {reviewsSummary.recent_reviews?.length > 0 ? (
                        <div className="p-3 p-md-4">
                            <p className="text-xs fw-semibold text-secondary-light text-uppercase mb-3" style={{ letterSpacing: '0.06em' }}>
                                Recent Reviews
                            </p>
                            <div className="d-flex flex-column gap-3">
                                {reviewsSummary.recent_reviews.slice(0, 5).map((review, idx) => {
                                    const initials = (review.reviewer_name || 'A')
                                        .trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
                                    const rating = parseFloat(review.rating);
                                    const reviewDate = formatIntlDate(review.created_at);
                                    const matchedVenue = venues.find((vn) => Number(vn.id) === Number(review.venue_id));
                                    const fullReviewVenueName = matchedVenue?.title || matchedVenue?.business_name || review.venue_name;

                                    return (
                                        <div
                                            key={review.id || idx}
                                            className="d-flex gap-3 p-3 border border-neutral-200"
                                            style={{ borderRadius: '14px', background: 'var(--hs-surface-muted)' }}
                                        >
                                            {/* Avatar */}
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-xs"
                                                style={{ width: '40px', height: '40px', backgroundColor: 'rgba(7,137,213,0.12)', color: '#0789d5', overflow: 'hidden' }}
                                            >
                                                {review.reviewer_avatar ? (
                                                    <img
                                                        src={review.reviewer_avatar.startsWith('http') ? review.reviewer_avatar : `${config.API_BASE_URL}/${review.reviewer_avatar.replace(/^\//, '')}`}
                                                        alt={review.reviewer_name}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={e => { e.target.style.display = 'none'; e.target.parentNode.textContent = initials; }}
                                                    />
                                                ) : initials}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-grow-1 overflow-hidden">
                                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                        <span className="fw-semibold text-primary-light text-sm">
                                                            {review.reviewer_name || 'Anonymous'}
                                                        </span>
                                                        {!isNaN(rating) && rating > 0 && (
                                                            <span
                                                                className="badge rounded-pill px-2 py-1 text-xs fw-bold d-inline-flex align-items-center gap-1"
                                                                style={{ backgroundColor: 'rgba(245,158,11,0.12)', color: '#d97706' }}
                                                            >
                                                                <Icon icon="solar:star-bold" style={{ fontSize: '11px' }} />
                                                                {rating.toFixed(1)}
                                                            </span>
                                                        )}
                                                        {fullReviewVenueName && (
                                                            <span className="text-xs text-secondary-light d-flex align-items-center gap-1" title={fullReviewVenueName}>
                                                                <Icon icon="solar:shop-2-linear" style={{ fontSize: '12px' }} />
                                                                {fullReviewVenueName}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {reviewDate && (
                                                        <span className="text-2xs text-secondary-light flex-shrink-0">{reviewDate}</span>
                                                    )}
                                                </div>
                                                {review.comment && (
                                                    <p className="text-xs text-secondary-light mb-0" style={{ lineHeight: 1.6 }}>
                                                        {review.comment}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center justify-content-center py-5 flex-column gap-2">
                            <Icon icon="solar:star-linear" className="text-secondary-light" style={{ fontSize: '32px' }} />
                            <span className="text-sm text-secondary-light">No reviews yet — they'll appear here once customers leave feedback.</span>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
};

export default BusinessOverviewLayer;
